import { FormEvent, useEffect, useMemo, useState } from 'react'
import {
  Archive,
  Bell,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  Command,
  Grid2X2,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  Sparkles,
  Users,
  X,
} from 'lucide-react'
import type { Session, User } from '@supabase/supabase-js'
import { createDemoData } from './demo'
import { isSupabaseConfigured, supabase } from './lib/supabase'
import type { AppData, Priority, Project, Section, Task, TeamMember, Workspace } from './types'

const priorityLabels: Record<Priority, string> = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend',
}

const projectColors = ['#7057ff', '#ff8a5b', '#2d9d78', '#2e7dff', '#d946ef']
type MainView = 'overview' | 'tasks' | 'calendar' | 'inbox' | 'settings'
type ProjectView = 'board' | 'list' | 'timeline' | 'archive'

function initials(name: string) {
  return name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()
}

function getStoredDemo() {
  const saved = localStorage.getItem('projektboard-demo')
  return saved ? (JSON.parse(saved) as AppData) : createDemoData()
}

async function loadSupabaseData(user: User): Promise<AppData | null> {
  if (!supabase) return null
  const { data: memberships, error: membershipError } = await supabase
    .from('workspace_members')
    .select('role, workspace_id, workspaces(id,name,slug)')
    .eq('user_id', user.id)
    .limit(1)
  if (membershipError) throw membershipError
  if (!memberships?.length) return null

  const membership = memberships[0] as unknown as { role: Workspace['role']; workspace_id: string; workspaces: { id: string; name: string; slug: string } }
  const workspace: Workspace = { ...membership.workspaces, role: membership.role }

  const [projectsResult, membersResult] = await Promise.all([
    supabase.from('projects').select('id,workspace_id,name,color,key').eq('workspace_id', workspace.id).is('archived_at', null).order('created_at'),
    supabase.from('workspace_member_profiles').select('id,display_name,email,avatar_url').eq('workspace_id', workspace.id),
  ])
  if (projectsResult.error) throw projectsResult.error
  if (membersResult.error) throw membersResult.error
  const projects = (projectsResult.data || []) as Project[]
  const projectIds = projects.map((project) => project.id)
  if (!projectIds.length) return { workspace, projects: [], sections: [], tasks: [], members: (membersResult.data || []) as TeamMember[] }

  const [sectionsResult, tasksResult] = await Promise.all([
    supabase.from('sections').select('id,project_id,name,position,color').in('project_id', projectIds).order('position'),
    supabase.from('tasks').select('id,project_id,section_id,title,description,priority,due_date,assignee_id,position,completed_at').in('project_id', projectIds).order('position'),
  ])
  if (sectionsResult.error) throw sectionsResult.error
  if (tasksResult.error) throw tasksResult.error
  return {
    workspace,
    projects,
    sections: (sectionsResult.data || []) as Section[],
    tasks: (tasksResult.data || []) as Task[],
    members: (membersResult.data || []) as TeamMember[],
  }
}

function AuthScreen({ onDemo }: { onDemo: () => void }) {
  const [mode, setMode] = useState<'login' | 'signup'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!supabase) return onDemo()
    setLoading(true)
    setMessage('')
    const result = mode === 'login'
      ? await supabase.auth.signInWithPassword({ email, password })
      : await supabase.auth.signUp({ email, password, options: { data: { display_name: email.split('@')[0] }, emailRedirectTo: window.location.origin } })
    setLoading(false)
    setMessage(result.error ? result.error.message : mode === 'signup' ? 'Bestätigungslink wurde versendet.' : '')
  }

  return (
    <main className="auth-shell">
      <section className="auth-brand">
        <div className="brand-mark"><Command size={22} /></div>
        <div className="auth-copy">
          <span className="eyebrow">Ein Ort für klare Zusammenarbeit</span>
          <h1>Vom Gedanken<br />zur <em>Erledigung.</em></h1>
          <p>Projekte, Aufgaben und Teamwissen in einem ruhigen, schnellen Arbeitsbereich.</p>
        </div>
        <div className="auth-preview" aria-hidden="true">
          <div className="preview-bar"><i /><i /><i /></div>
          <div className="preview-body">
            <div className="preview-nav" />
            <div className="preview-cols">
              {[0, 1, 2].map((column) => <div className="preview-col" key={column}>{[0, 1, 2].slice(0, column + 1).map((card) => <div className="preview-card" key={card} />)}</div>)}
            </div>
          </div>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <div className="mobile-brand"><Command size={18} /> Projektboard</div>
          <h2>{mode === 'login' ? 'Willkommen zurück' : 'Arbeitsbereich starten'}</h2>
          <p>{mode === 'login' ? 'Melde dich an und mach dort weiter, wo dein Team aufgehört hat.' : 'Erstelle deinen Zugang. Das erste Projekt ist in einer Minute bereit.'}</p>
          <form onSubmit={submit}>
            <label>E-Mail<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="du@unternehmen.de" required /></label>
            <label>Passwort<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Mindestens 8 Zeichen" minLength={8} required /></label>
            {message && <div className="form-message">{message}</div>}
            <button className="primary-button" disabled={loading}>{loading ? 'Einen Moment …' : mode === 'login' ? 'Anmelden' : 'Konto erstellen'}</button>
          </form>
          <button className="text-button" onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}>
            {mode === 'login' ? 'Noch kein Konto? Jetzt starten' : 'Schon dabei? Anmelden'}
          </button>
          {!isSupabaseConfigured && <button className="demo-button" onClick={onDemo}><Sparkles size={16} /> Interaktive Demo öffnen</button>}
          <small>Mit der Anmeldung stimmst du den Nutzungs- und Datenschutzbedingungen zu.</small>
        </div>
      </section>
    </main>
  )
}

function EmptyWorkspace({ user, onCreated }: { user: User; onCreated: (data: AppData) => void }) {
  const [name, setName] = useState('Mein Team')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  async function createWorkspace(event: FormEvent) {
    event.preventDefault()
    if (!supabase) return
    setLoading(true)
    setError('')
    const slug = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${crypto.randomUUID().slice(0, 5)}`
    const { data, error: rpcError } = await supabase.rpc('create_workspace_with_project', { workspace_name: name, workspace_slug: slug })
    if (rpcError) setError(rpcError.message)
    else {
      const loaded = await loadSupabaseData(user)
      if (loaded) onCreated(loaded)
    }
    setLoading(false)
    void data
  }
  return <div className="center-card"><div className="brand-mark"><Command size={22} /></div><h1>Dein erster Arbeitsbereich</h1><p>Gib deinem Team einen Namen. Du kannst später Mitglieder und weitere Projekte hinzufügen.</p><form onSubmit={createWorkspace}><label>Name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>{error && <div className="form-message">{error}</div>}<button className="primary-button" disabled={loading}>Arbeitsbereich erstellen</button></form></div>
}

function Avatar({ member, small = false }: { member?: TeamMember; small?: boolean }) {
  return <span className={`avatar ${small ? 'avatar-small' : ''}`} title={member?.display_name || 'Nicht zugewiesen'}>{member ? initials(member.display_name) : '?'}</span>
}

function TaskCard({ task, member, onOpen, onDropStart }: { task: Task; member?: TeamMember; onOpen: () => void; onDropStart: () => void }) {
  const due = task.due_date ? new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'short' }).format(new Date(task.due_date)) : null
  return <article className="task-card" draggable onDragStart={onDropStart} onClick={onOpen}>
    <div className="card-top"><span className={`priority-dot ${task.priority}`} /> <button aria-label="Mehr"><MoreHorizontal size={17} /></button></div>
    <h4>{task.title}</h4>
    {task.description && <p>{task.description}</p>}
    <div className="card-meta">
      <Avatar member={member} small />
      {due && <span className="due"><CalendarDays size={13} />{due}</span>}
    </div>
  </article>
}

function TaskDrawer({ task, data, onClose, onSave, onDelete }: { task: Task; data: AppData; onClose: () => void; onSave: (task: Task) => void; onDelete: () => void }) {
  const [draft, setDraft] = useState(task)
  const sections = data.sections.filter((section) => section.project_id === task.project_id)
  return <div className="drawer-layer" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <aside className="task-drawer">
      <header><span className="status-pill"><CheckCircle2 size={15} /> Aufgabe</span><button onClick={onClose} aria-label="Schließen"><X /></button></header>
      <input className="title-input" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
      <div className="drawer-grid">
        <label>Status<select value={draft.section_id} onChange={(event) => setDraft({ ...draft, section_id: event.target.value })}>{sections.map((section) => <option value={section.id} key={section.id}>{section.name}</option>)}</select></label>
        <label>Priorität<select value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value as Priority })}>{Object.entries(priorityLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        <label>Zuständig<select value={draft.assignee_id || ''} onChange={(event) => setDraft({ ...draft, assignee_id: event.target.value || null })}><option value="">Nicht zugewiesen</option>{data.members.map((member) => <option value={member.id} key={member.id}>{member.display_name}</option>)}</select></label>
        <label>Fällig am<input type="date" value={draft.due_date || ''} onChange={(event) => setDraft({ ...draft, due_date: event.target.value || null })} /></label>
      </div>
      <label className="description-label">Beschreibung<textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="Notizen, Links oder Details …" /></label>
      <div className="drawer-actions"><button className="danger-link" onClick={onDelete}>Aufgabe löschen</button><button className="primary-button" onClick={() => onSave(draft)}>Änderungen speichern</button></div>
    </aside>
  </div>
}

function InviteModal({ workspace, onClose }: { workspace: Workspace; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!supabase) {
      setMessage('Demo: Einladung vorgemerkt.')
      return
    }
    const { error } = await supabase.rpc('add_workspace_member_by_email', { target_workspace_id: workspace.id, target_email: email })
    setMessage(error ? error.message : 'Mitglied wurde hinzugefügt.')
  }
  return <div className="modal-layer" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className="modal"><header><div><span className="eyebrow">Team erweitern</span><h3>Mitglied einladen</h3></div><button onClick={onClose}><X /></button></header><p>Die Person muss bereits ein Projektboard-Konto mit dieser E-Mail-Adresse besitzen.</p><form onSubmit={submit}><label>E-Mail-Adresse<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@unternehmen.de" required /></label>{message && <div className="form-message">{message}</div>}<button className="primary-button">Mitglied hinzufügen</button></form></div></div>
}

function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured)
  const [demo, setDemo] = useState(false)
  const [data, setData] = useState<AppData | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [activeProjectId, setActiveProjectId] = useState('')
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null)
  const [quickAddSection, setQuickAddSection] = useState<string | null>(null)
  const [quickTitle, setQuickTitle] = useState('')
  const [search, setSearch] = useState('')
  const [mainView, setMainView] = useState<MainView>('tasks')
  const [projectView, setProjectView] = useState<ProjectView>('board')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => {
    if (!supabase) return
    supabase.auth.getSession().then(({ data: auth }) => setSession(auth.session)).finally(() => setAuthReady(true))
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setAuthReady(true)
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!authReady) return
    if (demo) {
      const next = getStoredDemo()
      setData(next)
      setActiveProjectId(next.projects[0]?.id || '')
      setLoading(false)
      return
    }
    if (!session?.user) {
      setLoading(false)
      setData(null)
      return
    }
    setLoading(true)
    loadSupabaseData(session.user).then((next) => {
      setData(next)
      setActiveProjectId(next?.projects[0]?.id || '')
    }).catch((error) => setToast(error.message)).finally(() => setLoading(false))
  }, [session, demo, authReady])

  useEffect(() => {
    if (demo && data) localStorage.setItem('projektboard-demo', JSON.stringify(data))
  }, [data, demo])

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(''), 3000)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const activeProject = data?.projects.find((project) => project.id === activeProjectId) || data?.projects[0]
  const activeSections = useMemo(() => data?.sections.filter((section) => section.project_id === activeProject?.id).sort((a, b) => a.position - b.position) || [], [data, activeProject])
  const activeTasks = useMemo(() => data?.tasks.filter((task) => task.project_id === activeProject?.id && task.title.toLowerCase().includes(search.toLowerCase())) || [], [data, activeProject, search])
  const searchedTasks = useMemo(() => data?.tasks.filter((task) => task.title.toLowerCase().includes(search.toLowerCase())) || [], [data, search])
  const selectedTask = data?.tasks.find((task) => task.id === selectedTaskId)
  const openTasks = searchedTasks.filter((task) => !task.completed_at)
  const completedTasks = searchedTasks.filter((task) => task.completed_at)
  const dueTasks = openTasks.filter((task) => task.due_date).sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
  const inboxTasks = openTasks.filter((task) => task.priority === 'urgent' || !task.assignee_id || (task.due_date && new Date(task.due_date) < new Date()))

  function projectFor(task: Task) {
    return data?.projects.find((project) => project.id === task.project_id)
  }

  function sectionFor(task: Task) {
    return data?.sections.find((section) => section.id === task.section_id)
  }

  function openTask(task: Task) {
    setActiveProjectId(task.project_id)
    setSelectedTaskId(task.id)
  }

  function navigate(view: MainView) {
    setMainView(view)
    setSidebarOpen(false)
  }

  function updateLocalTask(task: Task) {
    setData((current) => current ? { ...current, tasks: current.tasks.map((item) => item.id === task.id ? task : item) } : current)
  }

  async function saveTask(task: Task) {
    updateLocalTask(task)
    setSelectedTaskId(null)
    if (supabase && !demo) {
      const { error } = await supabase.from('tasks').update({ title: task.title, description: task.description, section_id: task.section_id, priority: task.priority, due_date: task.due_date, assignee_id: task.assignee_id }).eq('id', task.id)
      if (error) setToast(error.message)
    }
    setToast('Aufgabe gespeichert')
  }

  async function moveTask(sectionId: string) {
    if (!draggedTaskId || !data) return
    const task = data.tasks.find((item) => item.id === draggedTaskId)
    if (!task || task.section_id === sectionId) return
    const updated = { ...task, section_id: sectionId, completed_at: activeSections.at(-1)?.id === sectionId ? new Date().toISOString() : null }
    updateLocalTask(updated)
    if (supabase && !demo) await supabase.from('tasks').update({ section_id: sectionId, completed_at: updated.completed_at }).eq('id', task.id)
  }

  async function addTask(sectionId: string) {
    if (!quickTitle.trim() || !data || !activeProject) return
    const task: Task = { id: crypto.randomUUID(), project_id: activeProject.id, section_id: sectionId, title: quickTitle.trim(), description: '', priority: 'medium', due_date: null, assignee_id: null, position: activeTasks.filter((item) => item.section_id === sectionId).length, completed_at: null }
    if (supabase && !demo) {
      const { data: created, error } = await supabase.from('tasks').insert({ ...task, id: undefined }).select('id,project_id,section_id,title,description,priority,due_date,assignee_id,position,completed_at').single()
      if (error) return setToast(error.message)
      task.id = created.id
    }
    setData({ ...data, tasks: [...data.tasks, task] })
    setQuickTitle('')
    setQuickAddSection(null)
  }

  async function deleteTask() {
    if (!selectedTaskId || !data) return
    setData({ ...data, tasks: data.tasks.filter((task) => task.id !== selectedTaskId) })
    if (supabase && !demo) await supabase.from('tasks').delete().eq('id', selectedTaskId)
    setSelectedTaskId(null)
    setToast('Aufgabe gelöscht')
  }

  async function addProject(event: FormEvent) {
    event.preventDefault()
    if (!data) return
    const name = newProjectName.trim()
    if (!name) return
    const project: Project = { id: crypto.randomUUID(), workspace_id: data.workspace.id, name, color: projectColors[data.projects.length % projectColors.length], key: name.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase() || 'PRJ' }
    let projectId = project.id
    let newSections: Section[]
    if (supabase && !demo) {
      const { data: created, error } = await supabase.from('projects').insert({ workspace_id: project.workspace_id, name: project.name, color: project.color, key: project.key }).select('id').single()
      if (error) return setToast(error.message)
      projectId = created.id
      const { data: createdSections, error: sectionError } = await supabase
        .from('sections')
        .insert(['Backlog', 'In Arbeit', 'Review', 'Erledigt'].map((sectionName, position) => ({ project_id: projectId, name: sectionName, position, color: ['#a1a1aa', '#7057ff', '#f59e0b', '#2d9d78'][position] })))
        .select('id,project_id,name,position,color')
      if (sectionError) return setToast(sectionError.message)
      newSections = createdSections as Section[]
    } else {
      newSections = ['Backlog', 'In Arbeit', 'Review', 'Erledigt'].map((sectionName, position) => ({ id: crypto.randomUUID(), project_id: projectId, name: sectionName, position, color: ['#a1a1aa', '#7057ff', '#f59e0b', '#2d9d78'][position] }))
    }
    const next = { ...data, projects: [...data.projects, { ...project, id: projectId }], sections: [...data.sections, ...newSections] }
    setData(next)
    setActiveProjectId(projectId)
    setMainView('tasks')
    setNewProjectName('')
    setNewProjectOpen(false)
    setToast('Projekt erstellt')
  }

  if (!authReady || loading) return <div className="loading-screen"><div className="brand-mark"><Command /></div><span>Projektboard wird geladen …</span></div>
  if (!demo && !session) return <AuthScreen onDemo={() => setDemo(true)} />
  if (!data && session?.user) return <EmptyWorkspace user={session.user} onCreated={setData} />
  if (!data) return null

  return <div className="app-shell">
    {sidebarOpen && <button className="mobile-overlay" onClick={() => setSidebarOpen(false)} />}
    <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <div className="brand"><span className="brand-mark"><Command size={18} /></span><span>Projektboard</span><button className="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={18} /></button></div>
      <div className="workspace-menu-wrap">
        <button className="workspace-switch" aria-expanded={workspaceMenuOpen} onClick={() => setWorkspaceMenuOpen((open) => !open)}><span className="workspace-avatar">{initials(data.workspace.name)}</span><span><strong>{data.workspace.name}</strong><small>{data.members.length} Mitglieder</small></span><ChevronDown className={workspaceMenuOpen ? 'chevron-open' : ''} size={16} /></button>
        {workspaceMenuOpen && <div className="workspace-menu">
          <button onClick={() => { navigate('overview'); setWorkspaceMenuOpen(false) }}><LayoutDashboard size={16} /> Übersicht</button>
          <button onClick={() => { setInviteOpen(true); setWorkspaceMenuOpen(false) }}><Users size={16} /> Team verwalten</button>
          <button onClick={() => { navigate('settings'); setWorkspaceMenuOpen(false) }}><Settings size={16} /> Einstellungen</button>
        </div>}
      </div>
      <nav className="main-nav">
        <button className={mainView === 'overview' ? 'active' : ''} onClick={() => navigate('overview')}><LayoutDashboard />Übersicht</button>
        <button className={mainView === 'tasks' ? 'active' : ''} onClick={() => navigate('tasks')}><Grid2X2 />Meine Aufgaben<span>{openTasks.length}</span></button>
        <button className={mainView === 'calendar' ? 'active' : ''} onClick={() => navigate('calendar')}><CalendarDays />Kalender</button>
        <button className={mainView === 'inbox' ? 'active' : ''} onClick={() => navigate('inbox')}><Bell />Posteingang<span>{inboxTasks.length}</span></button>
      </nav>
      <div className="nav-section">
        <div className="nav-heading"><span>Projekte</span><button aria-label="Projekt hinzufügen" onClick={() => setNewProjectOpen(true)}><Plus size={16} /></button></div>
        {data.projects.map((project) => <button className={`project-link ${mainView === 'tasks' && project.id === activeProject?.id ? 'active' : ''}`} onClick={() => { setActiveProjectId(project.id); setMainView('tasks'); setSidebarOpen(false) }} key={project.id}><i style={{ background: project.color }} />{project.name}</button>)}
      </div>
      <div className="sidebar-bottom">
        <button onClick={() => setInviteOpen(true)}><Users />Team verwalten</button>
        <button className={mainView === 'settings' ? 'active' : ''} onClick={() => navigate('settings')}><Settings />Einstellungen</button>
        <button onClick={() => demo ? setDemo(false) : supabase?.auth.signOut()}><LogOut />Abmelden</button>
      </div>
    </aside>
    <main className="workspace-main">
      <header className="topbar">
        <button className="menu-button" onClick={() => setSidebarOpen(true)}><Menu /></button>
        <div className="search-box"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Aufgaben durchsuchen …" /><kbd>⌘ K</kbd></div>
        <div className="top-actions"><button className="icon-button" onClick={() => navigate('inbox')}><Bell size={19} />{inboxTasks.length > 0 && <i />}</button><div className="member-stack">{data.members.slice(0, 3).map((member) => <Avatar key={member.id} member={member} small />)}</div><button className="avatar user-avatar" onClick={() => navigate('settings')}>{initials(data.members[0]?.display_name || 'U')}</button></div>
      </header>
      <section className="content">
        {mainView === 'overview' && <>
          <div className="page-title-row"><div><span className="eyebrow">Arbeitsbereich</span><h1>Übersicht</h1><p>Alles Wichtige in {data.workspace.name} auf einen Blick.</p></div><button className="primary-button" onClick={() => navigate('tasks')}><Grid2X2 size={17} /> Zum Board</button></div>
          <div className="metric-grid">
            <article><span>Offene Aufgaben</span><strong>{openTasks.length}</strong><small>{dueTasks.length} mit Termin</small></article>
            <article><span>Erledigt</span><strong>{completedTasks.length}</strong><small>im gesamten Arbeitsbereich</small></article>
            <article><span>Projekte</span><strong>{data.projects.length}</strong><small>{data.members.length} Teammitglieder</small></article>
          </div>
          <div className="dashboard-grid">
            <section className="panel"><header><div><span className="eyebrow">Als Nächstes</span><h2>Anstehende Aufgaben</h2></div></header><div className="simple-list">{dueTasks.slice(0, 6).map((task) => <button key={task.id} onClick={() => openTask(task)}><span className={`priority-dot ${task.priority}`} /><span><strong>{task.title}</strong><small>{projectFor(task)?.name}</small></span><time>{task.due_date ? new Intl.DateTimeFormat('de-DE').format(new Date(task.due_date)) : ''}</time></button>)}{!dueTasks.length && <p className="empty-state">Keine Aufgaben mit Termin.</p>}</div></section>
            <section className="panel"><header><div><span className="eyebrow">Fortschritt</span><h2>Projekte</h2></div></header><div className="project-progress">{data.projects.map((project) => { const projectTasks = data.tasks.filter((task) => task.project_id === project.id); const done = projectTasks.filter((task) => task.completed_at).length; const progress = projectTasks.length ? Math.round(done / projectTasks.length * 100) : 0; return <button key={project.id} onClick={() => { setActiveProjectId(project.id); navigate('tasks') }}><span><i style={{ background: project.color }} />{project.name}</span><b>{progress}%</b><em><i style={{ width: `${progress}%`, background: project.color }} /></em></button> })}</div></section>
          </div>
        </>}

        {mainView === 'tasks' && <>
          <div className="page-title-row">
            <div><span className="eyebrow">Projekt</span><h1><i style={{ background: activeProject?.color }} />{activeProject?.name || 'Neues Projekt'}</h1><p>{activeTasks.filter((task) => !task.completed_at).length} offene Aufgaben · {data.members.length} Teammitglieder</p></div>
            <div className="title-actions"><button className="secondary-button" onClick={() => setInviteOpen(true)}><Users size={17} /> Teilen</button><button className="primary-button" onClick={() => activeSections[0] && setQuickAddSection(activeSections[0].id)}><Plus size={17} /> Aufgabe</button></div>
          </div>
          <div className="view-tabs"><button className={projectView === 'board' ? 'active' : ''} onClick={() => setProjectView('board')}><Grid2X2 size={16} /> Board</button><button className={projectView === 'list' ? 'active' : ''} onClick={() => setProjectView('list')}><ListChecks size={16} /> Liste</button><button className={projectView === 'timeline' ? 'active' : ''} onClick={() => setProjectView('timeline')}><CalendarDays size={16} /> Zeitplan</button><span /><button className={projectView === 'archive' ? 'active' : ''} onClick={() => setProjectView('archive')}><Archive size={16} /> Archiv</button></div>
          {projectView === 'board' && <div className="board">
            {activeSections.map((section) => {
              const sectionTasks = activeTasks.filter((task) => task.section_id === section.id && !task.completed_at)
              return <section className="board-column" key={section.id} onDragOver={(event) => event.preventDefault()} onDrop={() => moveTask(section.id)}>
                <header><span className="column-color" style={{ background: section.color }} /><h3>{section.name}</h3><b>{sectionTasks.length}</b><button><MoreHorizontal size={18} /></button></header>
                <div className="cards">
                  {sectionTasks.map((task) => <TaskCard key={task.id} task={task} member={data.members.find((member) => member.id === task.assignee_id)} onOpen={() => setSelectedTaskId(task.id)} onDropStart={() => setDraggedTaskId(task.id)} />)}
                  {quickAddSection === section.id ? <form className="quick-card" onSubmit={(event) => { event.preventDefault(); addTask(section.id) }}><textarea autoFocus value={quickTitle} onChange={(event) => setQuickTitle(event.target.value)} placeholder="Aufgabentitel eingeben …" /><div><button type="button" onClick={() => setQuickAddSection(null)}>Abbrechen</button><button className="primary-button">Hinzufügen</button></div></form> : <button className="add-card" onClick={() => setQuickAddSection(section.id)}><Plus size={16} /> Aufgabe hinzufügen</button>}
                </div>
              </section>
            })}
          </div>}
          {projectView === 'list' && <div className="data-list"><div className="data-list-head"><span>Aufgabe</span><span>Status</span><span>Priorität</span><span>Fällig</span></div>{activeTasks.filter((task) => !task.completed_at).map((task) => <button key={task.id} onClick={() => setSelectedTaskId(task.id)}><span><i className={`priority-dot ${task.priority}`} />{task.title}</span><span>{sectionFor(task)?.name}</span><span>{priorityLabels[task.priority]}</span><span>{task.due_date ? new Intl.DateTimeFormat('de-DE').format(new Date(task.due_date)) : '–'}</span></button>)}</div>}
          {projectView === 'timeline' && <div className="timeline"><div className="timeline-line" />{activeTasks.filter((task) => !task.completed_at).sort((a, b) => String(a.due_date || '9999').localeCompare(String(b.due_date || '9999'))).map((task) => <button key={task.id} onClick={() => setSelectedTaskId(task.id)}><time>{task.due_date ? new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'short' }).format(new Date(task.due_date)) : 'Offen'}</time><i style={{ background: activeProject?.color }} /><span><strong>{task.title}</strong><small>{sectionFor(task)?.name} · {priorityLabels[task.priority]}</small></span></button>)}</div>}
          {projectView === 'archive' && <div className="data-list"><div className="data-list-head"><span>Erledigte Aufgabe</span><span>Status</span><span>Priorität</span><span>Termin</span></div>{activeTasks.filter((task) => task.completed_at).map((task) => <button key={task.id} onClick={() => setSelectedTaskId(task.id)}><span><CheckCircle2 size={16} />{task.title}</span><span>Erledigt</span><span>{priorityLabels[task.priority]}</span><span>{task.due_date ? new Intl.DateTimeFormat('de-DE').format(new Date(task.due_date)) : '–'}</span></button>)}{!activeTasks.some((task) => task.completed_at) && <p className="empty-state">Noch keine erledigten Aufgaben.</p>}</div>}
        </>}

        {mainView === 'calendar' && <><div className="page-title-row"><div><span className="eyebrow">Termine</span><h1>Kalender</h1><p>Alle fälligen Aufgaben aus deinen Projekten.</p></div></div><div className="calendar-list">{dueTasks.map((task) => <button key={task.id} onClick={() => openTask(task)}><time><strong>{new Intl.DateTimeFormat('de-DE', { day: '2-digit' }).format(new Date(task.due_date!))}</strong><span>{new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(new Date(task.due_date!))}</span></time><i style={{ background: projectFor(task)?.color }} /><span><strong>{task.title}</strong><small>{projectFor(task)?.name} · {sectionFor(task)?.name}</small></span><b>{priorityLabels[task.priority]}</b></button>)}{!dueTasks.length && <p className="empty-state panel">Noch keine Termine eingetragen.</p>}</div></>}

        {mainView === 'inbox' && <><div className="page-title-row"><div><span className="eyebrow">Aktuelles</span><h1>Posteingang</h1><p>Aufgaben, die deine Aufmerksamkeit benötigen.</p></div></div><div className="notification-list">{inboxTasks.map((task) => <button key={task.id} onClick={() => openTask(task)}><span className={`priority-dot ${task.priority}`} /><span><strong>{task.title}</strong><small>{!task.assignee_id ? 'Noch nicht zugewiesen' : task.priority === 'urgent' ? 'Dringende Aufgabe' : 'Termin überschritten'} · {projectFor(task)?.name}</small></span><CheckCircle2 size={18} /></button>)}{!inboxTasks.length && <p className="empty-state panel">Alles erledigt – keine offenen Hinweise.</p>}</div></>}

        {mainView === 'settings' && <><div className="page-title-row"><div><span className="eyebrow">Arbeitsbereich</span><h1>Einstellungen</h1><p>{data.workspace.name} · Deine Rolle: {data.workspace.role}</p></div><button className="primary-button" onClick={() => setInviteOpen(true)}><Users size={17} /> Mitglied hinzufügen</button></div><div className="settings-grid"><section className="panel"><span className="eyebrow">Team</span><h2>{data.members.length} Mitglieder</h2><div className="member-list">{data.members.map((member) => <div key={member.id}><Avatar member={member} /><span><strong>{member.display_name}</strong><small>{member.email}</small></span></div>)}</div></section><section className="panel"><span className="eyebrow">Arbeitsbereich</span><h2>{data.workspace.name}</h2><dl><div><dt>Kennung</dt><dd>{data.workspace.slug}</dd></div><div><dt>Projekte</dt><dd>{data.projects.length}</dd></div><div><dt>Offene Aufgaben</dt><dd>{openTasks.length}</dd></div></dl></section></div></>}
      </section>
    </main>
    {selectedTask && <TaskDrawer task={selectedTask} data={data} onClose={() => setSelectedTaskId(null)} onSave={saveTask} onDelete={deleteTask} />}
    {inviteOpen && <InviteModal workspace={data.workspace} onClose={() => setInviteOpen(false)} />}
    {newProjectOpen && <div className="modal-layer" onMouseDown={(event) => event.target === event.currentTarget && setNewProjectOpen(false)}><div className="modal"><header><div><span className="eyebrow">Neues Projekt</span><h3>Projekt anlegen</h3></div><button onClick={() => setNewProjectOpen(false)}><X /></button></header><p>Das Projekt erhält automatisch ein Board mit Backlog, In Arbeit, Review und Erledigt.</p><form onSubmit={addProject}><label>Projektname<input autoFocus value={newProjectName} onChange={(event) => setNewProjectName(event.target.value)} placeholder="Zum Beispiel Website Relaunch" required /></label><button className="primary-button">Projekt erstellen</button></form></div></div>}
    {toast && <div className="toast"><Check size={16} />{toast}</div>}
  </div>
}

export default App
